import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro, Toast } from "../../hooks/useAdminApi";

/**
 * SÉRIES — a coleção de episódios (ex.: "Gênesis", 8 episódios).
 *
 * A série NÃO guarda vídeo: ela APONTA para conteúdos do acervo e define a ORDEM em que
 * tocam (o `index` do episódio). O mesmo conteúdo pode estar em mais de uma série, e é por
 * isso que excluir a série não pode apagar conteúdo nenhum — é o que a confirmação diz.
 *
 * A API fala em snake_case com prefixo (`serie_title`, `content_id`) e o front em camelCase.
 * A tradução acontece nas bordas — `paraFormulario` na leitura, `montarCorpo` na escrita —
 * para o resto da tela não ter que saber disso.
 */

/** GET /series/:id devolve `{ serie: { serie: {...} } }` — o router embrulha de novo o que o controller já embrulhou. */
const extrairSerie = (resposta) => resposta?.serie?.serie ?? resposta?.serie ?? null;

const paraFormulario = (s) => ({
  id: s.serie_id,
  title: s.serie_title || "",
  subtitle: s.serie_subtitle || "",
  description: s.serie_description || "",
  thumbnailUrl: s.serie_thumbnail_url || "",
  templateId: s.serie_template_id || "",
  access: s.serie_access || [],
  // A API NÃO devolve `serie_additional_info` em nenhum GET (o SerieDto não expõe o campo).
  // Editar começa com ele em branco, e o formulário só o envia se for preenchido — mandar ""
  // aqui sobrescreveria o HTML que já está salvo.
  additionalInfo: "",
  episodes: (s.serie_episodes || []).map((e) => ({
    contentId: e.episode_content.content_id,
    title: e.episode_content.content_title,
    thumbnailUrl: e.episode_content.content_thumbnail_url,
  })),
});

function montarCorpo(f, editando) {
  const corpo = {
    serie_title: f.title.trim(),
    serie_subtitle: f.subtitle.trim(),
    serie_description: f.description.trim(),
    serie_thumbnail_url: f.thumbnailUrl.trim(),
    serie_template_id: f.templateId,
    serie_access: f.access,
    // A ordem da lista É a ordem de reprodução. O index começa em 1, como no acervo atual.
    serie_episodes: f.episodes.map((e, i) => ({ content_id: e.contentId, index: i + 1 })),
  };

  // Na criação o campo é obrigatório (mesmo vazio). Na edição, mandar "" apagaria o HTML
  // salvo — como não dá pra ler o valor atual, só se envia o que foi digitado.
  if (!editando || f.additionalInfo.trim()) corpo.serie_additional_info = f.additionalInfo;

  return corpo;
}

export default function Series() {
  const { tema, setTema } = useOutletContext();
  const { erro, toast, rodar, limparErro } = useAdminState();

  const [series, setSeries] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [conteudos, setConteudos] = useState([]);
  const [busca, setBusca] = useState("");
  const [form, setForm] = useState(null);
  const [excluir, setExcluir] = useState(null);

  const carregar = useCallback(
    () =>
      rodar(async () => {
        const [listaSeries, listaTemplates, listaConteudos] = await Promise.all([
          apiGet(API_ROUTES.SERIES.list()),
          apiGet(API_ROUTES.TEMPLATES.list()),
          apiGet(API_ROUTES.CONTENTS.list()),
        ]);

        // A contagem vem na própria lista (serie_episodes_count). Antes não vinha, e a
        // tela buscava o DETALHE de cada série só para contar — um N+1 que crescia junto
        // com o acervo. Agora é uma requisição só.
        setSeries(
          (listaSeries.series || []).map((s) => ({
            ...s,
            episodios: s.serie_episodes_count ?? 0,
          }))
        );
        setTemplates(listaTemplates.templates || []);
        setConteudos(listaConteudos.contents || []);
      }),
    [rodar]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  /** As tags de acesso não têm cadastro próprio: as que existem são as que o acervo já usa. */
  const tagsAcesso = useMemo(() => {
    const tags = new Set();
    conteudos.forEach((c) => (c.content_access || []).forEach((a) => tags.add(a)));
    (series || []).forEach((s) => (s.serie_access || []).forEach((a) => tags.add(a)));
    return [...tags].sort();
  }, [conteudos, series]);

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return series || [];
    return (series || []).filter((s) =>
      `${s.serie_title} ${s.serie_subtitle}`.toLowerCase().includes(q)
    );
  }, [series, busca]);

  const abrirNova = () =>
    setForm({
      title: "",
      subtitle: "",
      description: "",
      thumbnailUrl: "",
      templateId: "",
      access: [],
      additionalInfo: "",
      episodes: [],
    });

  const abrirEdicao = (s) =>
    rodar(async () => {
      const detalhe = extrairSerie(await apiGet(API_ROUTES.SERIES.get(s.serie_id)));
      setForm(paraFormulario(detalhe));
    });

  const salvar = (f) =>
    rodar(async () => {
      const corpo = montarCorpo(f, Boolean(f.id));
      if (f.id) await apiSend(API_ROUTES.SERIES.update(f.id), "PUT", corpo);
      else await apiSend(API_ROUTES.SERIES.create(), "POST", corpo);
      setForm(null);
      await carregar();
    }, f.id ? "Série atualizada." : "Série criada.");

  const confirmarExclusao = (s) =>
    rodar(async () => {
      await apiSend(API_ROUTES.SERIES.delete(s.serie_id), "DELETE");
      setExcluir(null);
      await carregar();
    }, "Série excluída. Os conteúdos continuam no acervo.");

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Séries"
        subtitulo={
          <>
            Uma série é uma <strong>ordem de reprodução</strong> sobre conteúdos que já existem
            no acervo. Ela aponta para eles — não os contém.
          </>
        }
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <input
            className="adm__input"
            placeholder="Buscar série pelo título…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ flex: 1 }}
          />
          <button className="adm__btn" onClick={abrirNova}>
            + Nova série
          </button>
        </div>

        {!series && <p className="adm__vazio">Carregando…</p>}

        {series && visiveis.length === 0 && (
          <div className="adm__card">
            <p className="adm__vazio">
              {busca
                ? "Nenhuma série com esse título."
                : "Nenhuma série cadastrada. Crie a primeira para agrupar conteúdos em ordem."}
            </p>
          </div>
        )}

        {visiveis.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
              gap: 16,
            }}
          >
            {visiveis.map((s) => (
              <CardSerie
                key={s.serie_id}
                s={s}
                onAbrir={() => abrirEdicao(s)}
                onExcluir={() => setExcluir(s)}
              />
            ))}
          </div>
        )}
      </div>

      {form && (
        <ModalSerie
          form={form}
          templates={templates}
          conteudos={conteudos}
          tagsAcesso={tagsAcesso}
          onFechar={() => setForm(null)}
          onSalvar={salvar}
        />
      )}

      {excluir && (
        <ModalExcluir
          serie={excluir}
          onCancelar={() => setExcluir(null)}
          onConfirmar={() => confirmarExclusao(excluir)}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

function CardSerie({ s, onAbrir, onExcluir }) {
  return (
    <div
      className="adm__card"
      onClick={onAbrir}
      style={{ overflow: "hidden", cursor: "pointer" }}
    >
      <div
        style={{
          height: 122,
          background: "var(--elev)",
          backgroundImage: s.serie_thumbnail_url ? `url(${s.serie_thumbnail_url})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div style={{ padding: "12px 14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{s.serie_title}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExcluir();
            }}
            title="Excluir série"
            style={{
              background: "none",
              border: "none",
              color: "var(--text3)",
              cursor: "pointer",
              padding: 0,
              flex: "0 0 auto",
              fontSize: 14,
            }}
          >
            🗑
          </button>
        </div>

        <div style={{ fontSize: 11.5, color: "var(--text3)", margin: "3px 0 9px" }}>
          {s.episodios} {s.episodios === 1 ? "episódio" : "episódios"}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(s.serie_access || []).map((a) => (
            <span key={a} className="adm__chip adm__chip--red">
              {a}
            </span>
          ))}
          {/* Sem tag de acesso ninguém abre a série: `isBlocked` só é falso se alguma tag
              bater com a assinatura do usuário. */}
          {!(s.serie_access || []).length && (
            <span className="adm__chip adm__chip--orange">sem acesso</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalSerie({ form, templates, conteudos, tagsAcesso, onFechar, onSalvar }) {
  const [f, setF] = useState(form);
  const [seletor, setSeletor] = useState(false);

  const editando = Boolean(f.id);
  const set = (k, v) => setF((atual) => ({ ...atual, [k]: v }));

  const alternarAcesso = (t) =>
    set("access", f.access.includes(t) ? f.access.filter((x) => x !== t) : [...f.access, t]);

  const mover = (i, direcao) => {
    const j = i + direcao;
    if (j < 0 || j >= f.episodes.length) return;
    const lista = [...f.episodes];
    [lista[i], lista[j]] = [lista[j], lista[i]];
    set("episodes", lista);
  };

  const adicionar = (c) => {
    setF((atual) => ({
      ...atual,
      episodes: [
        ...atual.episodes,
        {
          contentId: c.content_id,
          title: c.content_title,
          thumbnailUrl: c.content_thumbnail_url,
        },
      ],
    }));
    setSeletor(false);
  };

  const remover = (contentId) =>
    set("episodes", f.episodes.filter((e) => e.contentId !== contentId));

  const valido =
    f.title.trim() &&
    f.subtitle.trim() &&
    f.description.trim() &&
    f.thumbnailUrl.trim() &&
    f.templateId &&
    f.access.length > 0 &&
    f.episodes.length > 0;

  return (
    <>
      <div className="adm__modalFundo" onClick={onFechar}>
        <div
          className="adm__modal"
          onClick={(e) => e.stopPropagation()}
          style={{ width: "min(720px, 100%)" }}
        >
          <h3>{editando ? "Editar série" : "Nova série"}</h3>
          <p className="adm__modalHint">
            A ordem dos episódios abaixo é a <strong>ordem de reprodução</strong> no app.
          </p>

          <div className="adm__campo">
            <label>Título</label>
            <input
              className="adm__input"
              value={f.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="ex.: Gênesis"
            />
          </div>

          <div className="adm__campo">
            <label>Subtítulo</label>
            <input
              className="adm__input"
              value={f.subtitle}
              onChange={(e) => set("subtitle", e.target.value)}
              placeholder="ex.: O começo de tudo"
            />
          </div>

          <div className="adm__campo">
            <label>Descrição</label>
            <textarea
              className="adm__input"
              rows={2}
              value={f.description}
              onChange={(e) => set("description", e.target.value)}
              style={{ resize: "vertical" }}
            />
          </div>

          <div className="adm__campo">
            <label>Thumbnail (URL)</label>
            <input
              className="adm__input"
              value={f.thumbnailUrl}
              onChange={(e) => set("thumbnailUrl", e.target.value)}
              placeholder="https://…"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div className="adm__campo">
              <label>Acesso</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                {tagsAcesso.map((t) => {
                  const on = f.access.includes(t);
                  return (
                    <button
                      key={t}
                      onClick={() => alternarAcesso(t)}
                      className={`adm__chip ${on ? "adm__chip--red" : ""}`}
                      style={{
                        cursor: "pointer",
                        border: on ? "1px solid var(--red)" : undefined,
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              {!f.access.length && (
                <p style={{ fontSize: 11.5, color: "var(--orange)", margin: "8px 0 0" }}>
                  Sem nenhuma tag, a série fica bloqueada para todo mundo — inclusive para quem
                  assina.
                </p>
              )}
            </div>

            <div className="adm__campo">
              <label>Template de venda</label>
              <select
                className="adm__select"
                value={f.templateId}
                onChange={(e) => set("templateId", e.target.value)}
              >
                <option value="">Selecione…</option>
                {templates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.template_name}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: 11, color: "var(--text3)", margin: "6px 0 0" }}>
                A página que aparece para quem não assinou.
              </p>
            </div>
          </div>

          <div className="adm__campo">
            <label>Informação adicional (HTML)</label>
            <textarea
              className="adm__input"
              rows={3}
              value={f.additionalInfo}
              onChange={(e) => set("additionalInfo", e.target.value)}
              placeholder="<p>Conteúdo rico que aparece embaixo do vídeo…</p>"
              style={{ resize: "vertical", fontFamily: '"Roboto Mono", monospace', fontSize: 12.5 }}
            />
            {editando && (
              <p style={{ fontSize: 11.5, color: "var(--orange)", margin: "6px 0 0" }}>
                A API não devolve este campo na leitura, então ele abre em branco. Em branco, o
                texto que já está salvo é <strong>mantido</strong>; qualquer coisa digitada aqui{" "}
                <strong>substitui</strong> o antigo.
              </p>
            )}
          </div>

          {/* ---------- episódios ---------- */}
          <div className="adm__campo">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <label style={{ margin: 0 }}>Episódios (ordem de reprodução)</label>
              <button className="adm__btnGhost" onClick={() => setSeletor(true)}>
                + Adicionar episódio
              </button>
            </div>

            {f.episodes.length === 0 ? (
              <div
                className="adm__vazio"
                style={{
                  border: "1px dashed var(--border)",
                  borderRadius: 9,
                  padding: 18,
                }}
              >
                Nenhum episódio ainda. Adicione o primeiro.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {f.episodes.map((e, i) => (
                  <div
                    key={e.contentId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: "var(--elev)",
                      border: "1px solid var(--border)",
                      borderRadius: 9,
                      padding: "8px 12px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "var(--text3)",
                        width: 20,
                      }}
                    >
                      {i + 1}
                    </span>
                    <span style={{ flex: 1, fontSize: 13 }}>{e.title}</span>
                    <button
                      className="adm__btnGhost"
                      onClick={() => mover(i, -1)}
                      disabled={i === 0}
                      aria-label="Subir"
                      style={{ padding: "3px 8px" }}
                    >
                      ▲
                    </button>
                    <button
                      className="adm__btnGhost"
                      onClick={() => mover(i, 1)}
                      disabled={i === f.episodes.length - 1}
                      aria-label="Descer"
                      style={{ padding: "3px 8px" }}
                    >
                      ▼
                    </button>
                    <button
                      className="adm__btnGhost"
                      onClick={() => remover(e.contentId)}
                      aria-label="Remover episódio"
                      style={{ padding: "3px 8px", color: "var(--cancel)" }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* A API recusa `serie_episodes: []` (exige ao menos 1) — e uma série sem episódio
                não reproduz nada. Barrar aqui evita o 400 e explica o motivo. */}
            {f.episodes.length === 0 && (
              <p style={{ fontSize: 11.5, color: "var(--orange)", margin: "8px 0 0" }}>
                Uma série precisa de pelo menos um episódio para ser salva.
              </p>
            )}
          </div>

          <div className="adm__modalAcoes">
            <button className="adm__btnGhost" onClick={onFechar}>
              Cancelar
            </button>
            <button className="adm__btn" disabled={!valido} onClick={() => onSalvar(f)}>
              Salvar série
            </button>
          </div>
        </div>
      </div>

      {/* Fora do fundo do formulário: dentro, todo clique aqui borbulharia até o onFechar dele. */}
      {seletor && (
        <SeletorConteudo
          conteudos={conteudos}
          jaAdicionados={f.episodes.map((e) => e.contentId)}
          onFechar={() => setSeletor(false)}
          onEscolher={adicionar}
        />
      )}
    </>
  );
}

function SeletorConteudo({ conteudos, jaAdicionados, onFechar, onEscolher }) {
  const [busca, setBusca] = useState("");

  const lista = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return conteudos
      .filter((c) => !jaAdicionados.includes(c.content_id))
      .filter((c) => !q || `${c.content_title} ${c.content_subtitle}`.toLowerCase().includes(q));
  }, [conteudos, jaAdicionados, busca]);

  return (
    <div className="adm__modalFundo" onClick={onFechar} style={{ zIndex: 200 }}>
      <div
        className="adm__modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(520px, 100%)" }}
      >
        <h3>Adicionar episódio</h3>
        <p className="adm__modalHint">
          O episódio é um conteúdo do acervo. Ele continua existindo por conta própria — a série
          só define onde ele entra na ordem.
        </p>

        <input
          className="adm__input"
          placeholder="Buscar conteúdo…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          autoFocus
        />

        <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 12 }}>
          {lista.length === 0 && (
            <p className="adm__vazio">
              {busca
                ? "Nenhum conteúdo com esse título."
                : "Todos os conteúdos já estão nesta série."}
            </p>
          )}

          {lista.map((c) => (
            <button
              key={c.content_id}
              onClick={() => onEscolher(c)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                width: "100%",
                textAlign: "left",
                background: "var(--elev)",
                border: "1px solid var(--border)",
                borderRadius: 9,
                padding: "8px 11px",
                marginBottom: 6,
                cursor: "pointer",
                color: "var(--text)",
                font: "inherit",
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  flex: "0 0 34px",
                  borderRadius: 6,
                  background: "var(--card)",
                  backgroundImage: c.content_thumbnail_url
                    ? `url(${c.content_thumbnail_url})`
                    : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13, fontWeight: 600 }}>
                  {c.content_title}
                </span>
                <span style={{ display: "block", fontSize: 11.5, color: "var(--text3)" }}>
                  {c.content_subtitle}
                </span>
              </span>
              <span style={{ display: "flex", gap: 4 }}>
                {(c.content_access || []).map((a) => (
                  <span key={a} className="adm__chip adm__chip--red">
                    {a}
                  </span>
                ))}
              </span>
            </button>
          ))}
        </div>

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

/** Excluir diz a CONSEQUÊNCIA: a série some das seções da home, os conteúdos ficam. */
function ModalExcluir({ serie, onCancelar, onConfirmar }) {
  return (
    <div className="adm__modalFundo" onClick={onCancelar} style={{ zIndex: 200 }}>
      <div
        className="adm__modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(430px, 100%)" }}
      >
        <h3 style={{ color: "var(--cancel)" }}>Excluir série</h3>
        <p className="adm__modalHint">
          Esta série tem <strong>{serie.episodios}</strong>{" "}
          {serie.episodios === 1 ? "episódio" : "episódios"}. Excluí-la remove a série e a tira
          das seções da home, mas <strong>NÃO</strong> os conteúdos — eles continuam no acervo e
          em outras séries.
        </p>

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onCancelar}>
            Cancelar
          </button>
          <button className="adm__btn adm__btnDanger" onClick={onConfirmar}>
            Excluir “{serie.serie_title}”
          </button>
        </div>
      </div>
    </div>
  );
}
